// packages/client/src/api/admins.ts
import "zod";

// packages/client/src/http/dispatcher.ts
import z from "zod";

// packages/client/src/http/interceptors.ts
var createHeadersInterceptor = (headers, requestHeaders) => {
  return {
    before({ init, url }) {
      for (const [key, value] of new Headers(requestHeaders)) {
        if (value !== void 0 && !init.headers.has(key)) {
          init.headers.set(key, value);
        }
      }
      for (const [key, value] of Object.entries(headers)) {
        if (value !== void 0 && !init.headers.has(key)) {
          init.headers.set(key, value);
        }
      }
      return { init, url };
    }
  };
};
var createBaseUrlInterceptor = (baseUrl) => {
  return {
    before({ init, url }) {
      if (url.protocol === "local:") {
        return {
          init,
          url: new URL(url.href.replace("local://", baseUrl))
        };
      }
      return { init, url };
    }
  };
};

// packages/client/src/http/request.ts
function template(templateString, templateVariables) {
  const nargs = /{([0-9a-zA-Z_]+)}/g;
  return templateString.replace(nargs, (match, key, index) => {
    if (templateString[index - 1] === "{" && templateString[index + match.length] === "}") {
      return key;
    }
    const result = key in templateVariables ? templateVariables[key] : null;
    return result === null || result === void 0 ? "" : String(result);
  });
}
var Serializer = class {
  input;
  props;
  constructor(input, props) {
    this.input = input;
    this.props = props;
  }
  serialize(path) {
    const params = this.props.inputParams.reduce(
      (acc, key) => {
        acc[key] = this.input[key];
        return acc;
      },
      {}
    );
    const url = new URL(template(path, params), `local://`);
    const headers = new Headers({});
    for (const header of this.props.inputHeaders) {
      headers.set(header, this.input[header]);
    }
    for (const key of this.props.inputQuery) {
      const value = this.input[key];
      if (value !== void 0) {
        if (Array.isArray(value)) {
          for (const item of value) {
            url.searchParams.append(key, String(item));
          }
        } else {
          url.searchParams.set(key, String(value));
        }
      }
    }
    return {
      body: this.getBody(),
      url,
      headers: this.getHeaders()
    };
  }
};
var JsonSerializer = class extends Serializer {
  getBody() {
    const body = {};
    if (this.props.inputBody.length === 1 && this.props.inputBody[0] === "$body") {
      return JSON.stringify(this.input.$body);
    }
    for (const prop of this.props.inputBody) {
      body[prop] = this.input[prop];
    }
    return JSON.stringify(body);
  }
  getHeaders() {
    return {
      "Content-Type": "application/json",
      Accept: "application/json"
    };
  }
};
var EmptySerializer = class extends Serializer {
  getBody() {
    return null;
  }
  getHeaders() {
    return {};
  }
};
function json(input, props) {
  return new JsonSerializer(input, props);
}
function empty(input, props) {
  return new EmptySerializer(input, props);
}
function toRequest(endpoint, serializer) {
  const [method, path] = endpoint.split(" ");
  const input = serializer.serialize(path);
  return {
    url: input.url,
    init: {
      method,
      headers: new Headers(input.headers),
      body: method === "GET" ? void 0 : input.body
    }
  };
}

// packages/client/src/http/parse-response.ts
import { parse } from "fast-content-type-parse";
function isBinaryContentType(contentType) {
  const type = contentType.toLowerCase();
  if (type.startsWith("image/")) {
    return true;
  }
  if (type.startsWith("audio/")) {
    return true;
  }
  if (type.startsWith("video/")) {
    return true;
  }
  switch (type) {
    case "application/pdf":
    case "application/zip":
    case "application/gzip":
    case "application/x-7z-compressed":
    case "application/x-tar":
    case "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet":
    case "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
    case "application/vnd.openxmlformats-officedocument.presentationml.presentation":
    case "application/vnd.ms-excel":
    case "application/vnd.ms-powerpoint":
    case "application/msword":
    case "application/octet-stream":
      return true;
    default:
      return false;
  }
}
async function buffered(response) {
  const contentType = response.headers.get("Content-Type");
  if (!contentType) {
    throw new Error("Content-Type header is missing");
  }
  if (response.status === 204) {
    return null;
  }
  const { type } = parse(contentType);
  if (isBinaryContentType(type)) {
    return response.blob();
  }
  if (type.startsWith("text/")) {
    return response.text();
  }
  switch (type) {
    case "application/json":
      return response.json();
    case "application/xml":
      return response.text();
    case "application/x-www-form-urlencoded": {
      const text = await response.text();
      return Object.fromEntries(new URLSearchParams(text));
    }
    case "multipart/form-data":
      return response.formData();
    default:
      throw new Error(`Unsupported content type: ${contentType}`);
  }
}

// packages/client/src/http/response.ts
var APIResponse = class {
  static status;
  status;
  data;
  headers;
  constructor(status, headers, data) {
    this.status = status;
    this.headers = headers;
    this.data = data;
  }
  static create(status, headers, data) {
    return new this(status, headers, data);
  }
};
var APIError = class extends APIResponse {
  static create(status, headers, data) {
    return new this(status, headers, data);
  }
};
var Ok = class _Ok extends APIResponse {
  static status = 200;
  constructor(headers, data) {
    super(_Ok.status, headers, data);
  }
  static create(status, headers, data) {
    return new this(headers, data);
  }
};
var Created = class _Created extends APIResponse {
  static status = 201;
  constructor(headers, data) {
    super(_Created.status, headers, data);
  }
  static create(status, headers, data) {
    return new this(headers, data);
  }
};
var Accepted = class _Accepted extends APIResponse {
  static status = 202;
  constructor(headers, data) {
    super(_Accepted.status, headers, data);
  }
  static create(status, headers, data) {
    return new this(headers, data);
  }
};
var NoContent = class _NoContent extends APIResponse {
  static status = 204;
  constructor(headers) {
    super(_NoContent.status, headers, null);
  }
  static create(status, headers) {
    return new this(headers);
  }
};
var BadRequest = class _BadRequest extends APIError {
  static status = 400;
  constructor(headers, data) {
    super(_BadRequest.status, headers, data);
  }
  static create(status, headers, data) {
    return new this(headers, data);
  }
};
var Unauthorized = class _Unauthorized extends APIError {
  static status = 401;
  constructor(headers, data) {
    super(_Unauthorized.status, headers, data);
  }
  static create(status, headers, data) {
    return new this(headers, data);
  }
};
var PaymentRequired = class _PaymentRequired extends APIError {
  static status = 402;
  constructor(headers, data) {
    super(_PaymentRequired.status, headers, data);
  }
  static create(status, headers, data) {
    return new this(headers, data);
  }
};
var Forbidden = class _Forbidden extends APIError {
  static status = 403;
  constructor(headers, data) {
    super(_Forbidden.status, headers, data);
  }
  static create(status, headers, data) {
    return new this(headers, data);
  }
};
var NotFound = class _NotFound extends APIError {
  static status = 404;
  constructor(headers, data) {
    super(_NotFound.status, headers, data);
  }
  static create(status, headers, data) {
    return new this(headers, data);
  }
};
var MethodNotAllowed = class _MethodNotAllowed extends APIError {
  static status = 405;
  constructor(headers, data) {
    super(_MethodNotAllowed.status, headers, data);
  }
  static create(status, headers, data) {
    return new this(headers, data);
  }
};
var NotAcceptable = class _NotAcceptable extends APIError {
  static status = 406;
  constructor(headers, data) {
    super(_NotAcceptable.status, headers, data);
  }
  static create(status, headers, data) {
    return new this(headers, data);
  }
};
var Conflict = class _Conflict extends APIError {
  static status = 409;
  constructor(headers, data) {
    super(_Conflict.status, headers, data);
  }
  static create(status, headers, data) {
    return new this(headers, data);
  }
};
var Gone = class _Gone extends APIError {
  static status = 410;
  constructor(headers, data) {
    super(_Gone.status, headers, data);
  }
  static create(status, headers, data) {
    return new this(headers, data);
  }
};
var PreconditionFailed = class _PreconditionFailed extends APIError {
  static status = 412;
  constructor(headers, data) {
    super(_PreconditionFailed.status, headers, data);
  }
  static create(status, headers, data) {
    return new this(headers, data);
  }
};
var UnprocessableEntity = class _UnprocessableEntity extends APIError {
  static status = 422;
  constructor(headers, data) {
    super(_UnprocessableEntity.status, headers, data);
  }
  static create(status, headers, data) {
    return new this(headers, data);
  }
};
var TooManyRequests = class _TooManyRequests extends APIError {
  static status = 429;
  constructor(headers, data) {
    super(_TooManyRequests.status, headers, data);
  }
  static create(status, headers, data) {
    return new this(headers, data);
  }
};
var PayloadTooLarge = class _PayloadTooLarge extends APIError {
  static status = 413;
  constructor(headers, data) {
    super(_PayloadTooLarge.status, headers, data);
  }
  static create(status, headers, data) {
    return new this(headers, data);
  }
};
var UnsupportedMediaType = class _UnsupportedMediaType extends APIError {
  static status = 415;
  constructor(headers, data) {
    super(_UnsupportedMediaType.status, headers, data);
  }
  static create(status, headers, data) {
    return new this(headers, data);
  }
};
var InternalServerError = class _InternalServerError extends APIError {
  static status = 500;
  constructor(headers, data) {
    super(_InternalServerError.status, headers, data);
  }
  static create(status, headers, data) {
    return new this(headers, data);
  }
};
var NotImplemented = class _NotImplemented extends APIError {
  static status = 501;
  constructor(headers, data) {
    super(_NotImplemented.status, headers, data);
  }
  static create(status, headers, data) {
    return new this(headers, data);
  }
};
var BadGateway = class _BadGateway extends APIError {
  static status = 502;
  constructor(headers, data) {
    super(_BadGateway.status, headers, data);
  }
  static create(status, headers, data) {
    return new this(headers, data);
  }
};
var ServiceUnavailable = class _ServiceUnavailable extends APIError {
  static status = 503;
  constructor(headers, data) {
    super(_ServiceUnavailable.status, headers, data);
  }
  static create(status, headers, data) {
    return new this(headers, data);
  }
};
var GatewayTimeout = class _GatewayTimeout extends APIError {
  static status = 504;
  constructor(headers, data) {
    super(_GatewayTimeout.status, headers, data);
  }
  static create(status, headers, data) {
    return new this(headers, data);
  }
};

// packages/client/src/http/dispatcher.ts
var fetchType = z.function().args(z.custom()).returns(z.promise(z.custom())).optional();
async function parse2(outputs10, response, mapper) {
  let output = null;
  let parser = buffered;
  for (const outputType of outputs10) {
    if ("parser" in outputType) {
      parser = outputType.parser;
      if (isTypeOf(outputType.type, APIResponse)) {
        if (response.status === outputType.type.status) {
          output = outputType.type;
          break;
        }
      }
    } else if (isTypeOf(outputType, APIResponse)) {
      if (response.status === outputType.status) {
        output = outputType;
        break;
      }
    }
  }
  if (response.ok) {
    const data = await parser(response);
    const mapped = mapper ? mapper(data) : data;
    const apiresponse = (output || APIResponse).create(
      response.status,
      response.headers,
      mapped
    );
    return apiresponse;
  }
  throw (output || APIError).create(
    response.status,
    response.headers,
    await parser(response)
  );
}
function isTypeOf(instance, baseType) {
  if (instance === baseType) {
    return true;
  }
  const prototype = Object.getPrototypeOf(instance);
  if (prototype === null) {
    return false;
  }
  return isTypeOf(prototype, baseType);
}
var Dispatcher = class {
  #interceptors = [];
  #fetch;
  constructor(interceptors, fetch2) {
    this.#interceptors = interceptors;
    this.#fetch = fetch2;
  }
  async send(config, outputs10, signal, mapper) {
    for (const interceptor of this.#interceptors) {
      if (interceptor.before) {
        config = await interceptor.before(config);
      }
    }
    const init = signal === void 0 ? config.init : { ...config.init, signal };
    let response = await (this.#fetch ?? fetch)(new Request(config.url, init));
    for (let i = this.#interceptors.length - 1; i >= 0; i--) {
      const interceptor = this.#interceptors[i];
      if (interceptor.after) {
        response = await interceptor.after(response.clone());
      }
    }
    if (mapper) {
      return await parse2(outputs10, response, mapper);
    }
    return await parse2(outputs10, response);
  }
};

// packages/client/src/inputs/admins.ts
import { z as z2 } from "zod";
var listAdminsSchema = z2.object({
  page: z2.coerce.number().int().gt(0).optional().default(1),
  pageSize: z2.coerce.number().int().min(1).max(1e3).optional().default(50),
  query: z2.union([z2.string(), z2.string(), z2.string()]).optional(),
  sortBy: z2.enum(["firstName", "lastName", "email", "createdAt"]).optional().default("createdAt"),
  sortOrder: z2.enum(["asc", "desc"]).optional().default("desc")
});
var getCurrentUserSchema = z2.object({}).catchall(z2.unknown());
var updateCurrentAdminSchema = z2.object({
  firstName: z2.string().optional(),
  lastName: z2.string().optional()
});
var createAdminSchema = z2.object({
  email: z2.string().email(),
  firstName: z2.string(),
  lastName: z2.string()
});
var updateAdminSchema = z2.object({
  email: z2.string().email().optional(),
  firstName: z2.string().optional(),
  lastName: z2.string().optional(),
  id: z2.string()
});
var deleteAdminSchema = z2.object({ id: z2.string() });
var listDocumentReviewsSchema = z2.object({
  page: z2.coerce.number().int().gt(0).optional().default(1),
  pageSize: z2.coerce.number().int().min(1).max(1e3).optional().default(50),
  sortBy: z2.enum(["createdAt", "updatedAt"]).optional().default("createdAt"),
  sortOrder: z2.enum(["asc", "desc"]).optional().default("desc")
});
var getDocumentReviewSchema = z2.object({ offerId: z2.string().uuid() });
var approveOfferDocumentSchema = z2.object({
  documentId: z2.string().uuid()
});
var rejectOfferDocumentSchema = z2.object({
  reason: z2.string().optional(),
  documentId: z2.string().uuid()
});
var listTTReviewsSchema = z2.object({
  page: z2.coerce.number().int().gt(0).optional().default(1),
  pageSize: z2.coerce.number().int().min(1).max(1e3).optional().default(50),
  sortBy: z2.string().optional().default("createdAt"),
  sortOrder: z2.enum(["asc", "desc"]).optional().default("desc")
});
var approveTTReviewSchema = z2.object({ offerId: z2.string().uuid() });
var rejectTTReviewSchema = z2.object({
  reason: z2.string().optional(),
  offerId: z2.string().uuid()
});
var getOfferTimelineSchema = z2.object({ id: z2.string().uuid() });
var getSignedDocumentLinksSchema = z2.object({
  submissionId: z2.string()
});

// packages/client/src/api/admins.ts
var admins_default = {
  "GET /admins": {
    schema: listAdminsSchema,
    output: [
      Ok,
      BadRequest,
      Unauthorized,
      Forbidden,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "GET /admins",
        empty(input, {
          inputHeaders: [],
          inputQuery: ["page", "pageSize", "query", "sortBy", "sortOrder"],
          inputBody: [],
          inputParams: []
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "GET /admin/me": {
    schema: getCurrentUserSchema,
    output: [
      Ok,
      BadRequest,
      Unauthorized,
      Forbidden,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "GET /admin/me",
        empty(input, {
          inputHeaders: [],
          inputQuery: [],
          inputBody: [],
          inputParams: []
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "PATCH /admin/me": {
    schema: updateCurrentAdminSchema,
    output: [
      Ok,
      BadRequest,
      Unauthorized,
      Forbidden,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "PATCH /admin/me",
        json(input, {
          inputHeaders: [],
          inputQuery: [],
          inputBody: ["firstName", "lastName"],
          inputParams: []
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "POST /admin": {
    schema: createAdminSchema,
    output: [
      Created,
      BadRequest,
      Unauthorized,
      Forbidden,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "POST /admin",
        json(input, {
          inputHeaders: [],
          inputQuery: [],
          inputBody: ["email", "firstName", "lastName"],
          inputParams: []
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "PATCH /admin/{id}": {
    schema: updateAdminSchema,
    output: [
      Ok,
      BadRequest,
      Unauthorized,
      Forbidden,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "PATCH /admin/{id}",
        json(input, {
          inputHeaders: [],
          inputQuery: [],
          inputBody: ["email", "firstName", "lastName"],
          inputParams: ["id"]
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "DELETE /admin/{id}": {
    schema: deleteAdminSchema,
    output: [
      Ok,
      BadRequest,
      Unauthorized,
      Forbidden,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "DELETE /admin/{id}",
        empty(input, {
          inputHeaders: [],
          inputQuery: [],
          inputBody: [],
          inputParams: ["id"]
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "GET /admin/document-reviews": {
    schema: listDocumentReviewsSchema,
    output: [
      Ok,
      BadRequest,
      Unauthorized,
      Forbidden,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "GET /admin/document-reviews",
        empty(input, {
          inputHeaders: [],
          inputQuery: ["page", "pageSize", "sortBy", "sortOrder"],
          inputBody: [],
          inputParams: []
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "GET /admin/document-reviews/{offerId}": {
    schema: getDocumentReviewSchema,
    output: [
      Ok,
      BadRequest,
      Unauthorized,
      Forbidden,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "GET /admin/document-reviews/{offerId}",
        empty(input, {
          inputHeaders: [],
          inputQuery: [],
          inputBody: [],
          inputParams: ["offerId"]
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "PATCH /admin/offer-documents/{documentId}/approve": {
    schema: approveOfferDocumentSchema,
    output: [
      Ok,
      BadRequest,
      Unauthorized,
      Forbidden,
      Conflict,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "PATCH /admin/offer-documents/{documentId}/approve",
        empty(input, {
          inputHeaders: [],
          inputQuery: [],
          inputBody: [],
          inputParams: ["documentId"]
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "PATCH /admin/offer-documents/{documentId}/reject": {
    schema: rejectOfferDocumentSchema,
    output: [
      Ok,
      BadRequest,
      Unauthorized,
      Forbidden,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "PATCH /admin/offer-documents/{documentId}/reject",
        json(input, {
          inputHeaders: [],
          inputQuery: [],
          inputBody: ["reason"],
          inputParams: ["documentId"]
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "GET /admin/tt-reviews": {
    schema: listTTReviewsSchema,
    output: [
      Ok,
      BadRequest,
      Unauthorized,
      Forbidden,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "GET /admin/tt-reviews",
        empty(input, {
          inputHeaders: [],
          inputQuery: ["page", "pageSize", "sortBy", "sortOrder"],
          inputBody: [],
          inputParams: []
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "POST /admin/tt-reviews/{offerId}/approve": {
    schema: approveTTReviewSchema,
    output: [
      Ok,
      BadRequest,
      Unauthorized,
      Forbidden,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "POST /admin/tt-reviews/{offerId}/approve",
        empty(input, {
          inputHeaders: [],
          inputQuery: [],
          inputBody: [],
          inputParams: ["offerId"]
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "POST /admin/tt-reviews/{offerId}/reject": {
    schema: rejectTTReviewSchema,
    output: [
      Ok,
      BadRequest,
      Unauthorized,
      Forbidden,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "POST /admin/tt-reviews/{offerId}/reject",
        json(input, {
          inputHeaders: [],
          inputQuery: [],
          inputBody: ["reason"],
          inputParams: ["offerId"]
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "GET /admin/offers/{id}/timeline": {
    schema: getOfferTimelineSchema,
    output: [
      Ok,
      BadRequest,
      Unauthorized,
      Forbidden,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "GET /admin/offers/{id}/timeline",
        empty(input, {
          inputHeaders: [],
          inputQuery: [],
          inputBody: [],
          inputParams: ["id"]
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "GET /admin/signing/{submissionId}/document": {
    schema: getSignedDocumentLinksSchema,
    output: [
      Ok,
      BadRequest,
      Unauthorized,
      Forbidden,
      NotFound,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "GET /admin/signing/{submissionId}/document",
        empty(input, {
          inputHeaders: [],
          inputQuery: [],
          inputBody: [],
          inputParams: ["submissionId"]
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  }
};

// packages/client/src/api/auth.ts
import "zod";

// packages/client/src/inputs/auth.ts
import { z as z4 } from "zod";
var changeOwnPasswordSchema = z4.object({ newPassword: z4.string() });
var firebaseAuthActionSchema = z4.object({
  mode: z4.enum(["resetPassword", "verifyEmail"]),
  oobCode: z4.string(),
  apiKey: z4.string(),
  lang: z4.string().optional()
});
var forgotPasswordSchema = z4.object({ email: z4.string().email() });
var passwordChangedSchema = z4.object({ email: z4.string().email() });

// packages/client/src/api/auth.ts
var auth_default = {
  "POST /me/password": {
    schema: changeOwnPasswordSchema,
    output: [
      NoContent,
      BadRequest,
      Unauthorized,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "POST /me/password",
        json(input, {
          inputHeaders: [],
          inputQuery: [],
          inputBody: ["newPassword"],
          inputParams: []
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "GET /auth/action": {
    schema: firebaseAuthActionSchema,
    output: [
      APIResponse,
      BadRequest,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "GET /auth/action",
        empty(input, {
          inputHeaders: [],
          inputQuery: ["mode", "oobCode", "apiKey", "lang"],
          inputBody: [],
          inputParams: []
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "POST /auth/forgot-password": {
    schema: forgotPasswordSchema,
    output: [
      Ok,
      BadRequest,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "POST /auth/forgot-password",
        json(input, {
          inputHeaders: [],
          inputQuery: [],
          inputBody: ["email"],
          inputParams: []
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "POST /auth/password-changed": {
    schema: passwordChangedSchema,
    output: [
      Ok,
      BadRequest,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "POST /auth/password-changed",
        json(input, {
          inputHeaders: [],
          inputQuery: [],
          inputBody: ["email"],
          inputParams: []
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  }
};

// packages/client/src/api/customers.ts
import "zod";

// packages/client/src/inputs/customers.ts
import { z as z6 } from "zod";
var listCustomersSchema = z6.object({
  page: z6.coerce.number().int().gt(0).optional().default(1),
  pageSize: z6.coerce.number().int().min(1).max(1e3).optional().default(50),
  query: z6.union([z6.string(), z6.string(), z6.string()]).optional(),
  sortBy: z6.enum(["name", "email", "createdAt", "approved"]).optional().default("createdAt"),
  sortOrder: z6.enum(["asc", "desc"]).optional().default("desc")
});
var getCurrentCustomerSchema = z6.object({}).catchall(z6.unknown());
var updateCustomerSchema = z6.object({
  bankCertificate: z6.string().optional(),
  commercialRegistrationFile: z6.string().optional(),
  companyLogo: z6.string().optional(),
  firstName: z6.string().optional(),
  jobTitle: z6.string().optional(),
  lastName: z6.string().optional(),
  name: z6.string().optional(),
  nationalAddressCertificate: z6.string().optional(),
  phoneNumber: z6.string().optional(),
  signatoryEmail: z6.string().email().optional(),
  signatoryFirstName: z6.string().optional(),
  signatoryJobTitle: z6.string().optional(),
  signatoryLastName: z6.string().optional(),
  taxCertificate: z6.string().optional(),
  zakaCertificate: z6.string().optional()
});
var acceptCustomerTermsSchema = z6.object({}).catchall(z6.unknown());
var getProfileSchema = z6.object({}).catchall(z6.unknown());
var getCustomerDocumentsByIdSchema = z6.object({ id: z6.string() });
var getCustomerDocumentsSchema = z6.object({}).catchall(z6.unknown());
var createCustomerRequiredDocumentSchema = z6.object({
  fileId: z6.string().optional(),
  name: z6.string(),
  reason: z6.string(),
  id: z6.string()
});
var resolveCustomerRequiredDocumentSchema = z6.object({
  id: z6.string(),
  fileId: z6.string()
});
var createCustomerSchema = z6.object({
  email: z6.string().email(),
  id: z6.string(),
  name: z6.string(),
  termsAccepted: z6.boolean().default(true)
});
var updateCustomerByIdSchema = z6.object({
  address: z6.union([z6.string(), z6.string().default("")]).optional(),
  commercialRegistrationNumber: z6.union([z6.string(), z6.string().default("")]).optional(),
  email: z6.string().email().optional(),
  name: z6.string().optional(),
  id: z6.string()
});
var deleteCustomerSchema = z6.object({ id: z6.string() });
var approveCustomerSchema = z6.object({ id: z6.string() });
var rejectCustomerSchema = z6.object({
  reason: z6.string().optional(),
  id: z6.string()
});

// packages/client/src/api/customers.ts
var customers_default = {
  "GET /customers": {
    schema: listCustomersSchema,
    output: [
      Ok,
      BadRequest,
      Unauthorized,
      Forbidden,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "GET /customers",
        empty(input, {
          inputHeaders: [],
          inputQuery: ["page", "pageSize", "query", "sortBy", "sortOrder"],
          inputBody: [],
          inputParams: []
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "GET /customer/me": {
    schema: getCurrentCustomerSchema,
    output: [
      Ok,
      BadRequest,
      Unauthorized,
      Forbidden,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "GET /customer/me",
        empty(input, {
          inputHeaders: [],
          inputQuery: [],
          inputBody: [],
          inputParams: []
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "PATCH /customer/me": {
    schema: updateCustomerSchema,
    output: [
      Ok,
      BadRequest,
      Unauthorized,
      Forbidden,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "PATCH /customer/me",
        json(input, {
          inputHeaders: [],
          inputQuery: [],
          inputBody: [
            "bankCertificate",
            "commercialRegistrationFile",
            "companyLogo",
            "firstName",
            "jobTitle",
            "lastName",
            "name",
            "nationalAddressCertificate",
            "phoneNumber",
            "signatoryEmail",
            "signatoryFirstName",
            "signatoryJobTitle",
            "signatoryLastName",
            "taxCertificate",
            "zakaCertificate"
          ],
          inputParams: []
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "POST /customer/me/accept-terms": {
    schema: acceptCustomerTermsSchema,
    output: [
      Ok,
      BadRequest,
      Unauthorized,
      Forbidden,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "POST /customer/me/accept-terms",
        empty(input, {
          inputHeaders: [],
          inputQuery: [],
          inputBody: [],
          inputParams: []
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "GET /customer/me/profile": {
    schema: getProfileSchema,
    output: [
      Ok,
      BadRequest,
      Unauthorized,
      Forbidden,
      NotFound,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "GET /customer/me/profile",
        empty(input, {
          inputHeaders: [],
          inputQuery: [],
          inputBody: [],
          inputParams: []
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "GET /customer/{id}/documents": {
    schema: getCustomerDocumentsByIdSchema,
    output: [
      Ok,
      BadRequest,
      Unauthorized,
      Forbidden,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "GET /customer/{id}/documents",
        empty(input, {
          inputHeaders: [],
          inputQuery: [],
          inputBody: [],
          inputParams: ["id"]
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "GET /customer/me/documents": {
    schema: getCustomerDocumentsSchema,
    output: [
      Ok,
      BadRequest,
      Unauthorized,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "GET /customer/me/documents",
        empty(input, {
          inputHeaders: [],
          inputQuery: [],
          inputBody: [],
          inputParams: []
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "POST /customer/{id}/request-documents": {
    schema: createCustomerRequiredDocumentSchema,
    output: [
      Ok,
      BadRequest,
      Unauthorized,
      Forbidden,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "POST /customer/{id}/request-documents",
        json(input, {
          inputHeaders: [],
          inputQuery: [],
          inputBody: ["fileId", "name", "reason"],
          inputParams: ["id"]
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "PATCH /customer/{id}/request-documents/{fileId}/resolve": {
    schema: resolveCustomerRequiredDocumentSchema,
    output: [
      Ok,
      BadRequest,
      Unauthorized,
      Forbidden,
      NotFound,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "PATCH /customer/{id}/request-documents/{fileId}/resolve",
        empty(input, {
          inputHeaders: [],
          inputQuery: [],
          inputBody: [],
          inputParams: ["id", "fileId"]
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "POST /customer": {
    schema: createCustomerSchema,
    output: [
      Ok,
      BadRequest,
      Unauthorized,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "POST /customer",
        json(input, {
          inputHeaders: [],
          inputQuery: [],
          inputBody: ["email", "id", "name", "termsAccepted"],
          inputParams: []
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "PATCH /customer/{id}": {
    schema: updateCustomerByIdSchema,
    output: [
      Ok,
      BadRequest,
      Unauthorized,
      Forbidden,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "PATCH /customer/{id}",
        json(input, {
          inputHeaders: [],
          inputQuery: [],
          inputBody: [
            "address",
            "commercialRegistrationNumber",
            "email",
            "name"
          ],
          inputParams: ["id"]
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "DELETE /customer/{id}": {
    schema: deleteCustomerSchema,
    output: [
      Ok,
      BadRequest,
      Unauthorized,
      Forbidden,
      Conflict,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "DELETE /customer/{id}",
        empty(input, {
          inputHeaders: [],
          inputQuery: [],
          inputBody: [],
          inputParams: ["id"]
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "PATCH /customer/{id}/approve": {
    schema: approveCustomerSchema,
    output: [
      Ok,
      BadRequest,
      Unauthorized,
      Forbidden,
      Conflict,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "PATCH /customer/{id}/approve",
        empty(input, {
          inputHeaders: [],
          inputQuery: [],
          inputBody: [],
          inputParams: ["id"]
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "PATCH /customer/{id}/reject": {
    schema: rejectCustomerSchema,
    output: [
      Ok,
      BadRequest,
      Unauthorized,
      Forbidden,
      Conflict,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "PATCH /customer/{id}/reject",
        json(input, {
          inputHeaders: [],
          inputQuery: [],
          inputBody: ["reason"],
          inputParams: ["id"]
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  }
};

// packages/client/src/api/files.ts
import "zod";

// packages/client/src/inputs/files.ts
import { z as z8 } from "zod";
var getSignedUrlSchema = z8.object({
  name: z8.string(),
  size: z8.number().optional(),
  type: z8.enum([
    "application/pdf",
    "image/png",
    "image/jpeg",
    "image/jpg",
    "application/dicom"
  ])
});
var markFileAsUsedSchema = z8.object({ id: z8.string() });
var getFilesSchema = z8.object({ userId: z8.string().optional() });
var getFileByIdSchema = z8.object({ id: z8.string() });
var getFileAccessSchema = z8.object({
  id: z8.string(),
  purpose: z8.enum(["view", "download"])
});

// packages/client/src/api/files.ts
var files_default = {
  "POST /signed-url": {
    schema: getSignedUrlSchema,
    output: [
      Ok,
      BadRequest,
      Unauthorized,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "POST /signed-url",
        json(input, {
          inputHeaders: [],
          inputQuery: [],
          inputBody: ["name", "size", "type"],
          inputParams: []
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "POST /files/{id}/used": {
    schema: markFileAsUsedSchema,
    output: [
      Created,
      BadRequest,
      Unauthorized,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "POST /files/{id}/used",
        empty(input, {
          inputHeaders: [],
          inputQuery: [],
          inputBody: [],
          inputParams: ["id"]
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "GET /files": {
    schema: getFilesSchema,
    output: [
      Ok,
      BadRequest,
      Unauthorized,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "GET /files",
        empty(input, {
          inputHeaders: [],
          inputQuery: ["userId"],
          inputBody: [],
          inputParams: []
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "GET /files/{id}": {
    schema: getFileByIdSchema,
    output: [
      Ok,
      BadRequest,
      Unauthorized,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "GET /files/{id}",
        empty(input, {
          inputHeaders: [],
          inputQuery: [],
          inputBody: [],
          inputParams: ["id"]
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "GET /files/{id}/access": {
    schema: getFileAccessSchema,
    output: [
      Ok,
      BadRequest,
      Unauthorized,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "GET /files/{id}/access",
        empty(input, {
          inputHeaders: [],
          inputQuery: ["purpose"],
          inputBody: [],
          inputParams: ["id"]
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  }
};

// packages/client/src/api/notifications.ts
import "zod";

// packages/client/src/inputs/notifications.ts
import { z as z10 } from "zod";
var listNotificationsSchema = z10.object({
  read: z10.union([
    z10.string().optional().default("false"),
    z10.boolean().optional().default(false)
  ]).optional(),
  page: z10.coerce.number().int().gt(0).optional().default(1),
  pageSize: z10.coerce.number().int().min(1).max(1e3).optional().default(50)
});
var markNotificationAsReadSchema = z10.object({ id: z10.string().uuid() });
var markAllNotificationsAsReadSchema = z10.object({}).catchall(z10.unknown());

// packages/client/src/api/notifications.ts
var notifications_default = {
  "GET /notifications": {
    schema: listNotificationsSchema,
    output: [
      Ok,
      BadRequest,
      Unauthorized,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "GET /notifications",
        empty(input, {
          inputHeaders: [],
          inputQuery: ["read", "page", "pageSize"],
          inputBody: [],
          inputParams: []
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "PUT /notifications/{id}/read": {
    schema: markNotificationAsReadSchema,
    output: [
      Ok,
      BadRequest,
      Unauthorized,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "PUT /notifications/{id}/read",
        empty(input, {
          inputHeaders: [],
          inputQuery: [],
          inputBody: [],
          inputParams: ["id"]
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "PUT /notifications/mark-all-read": {
    schema: markAllNotificationsAsReadSchema,
    output: [
      Ok,
      BadRequest,
      Unauthorized,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "PUT /notifications/mark-all-read",
        empty(input, {
          inputHeaders: [],
          inputQuery: [],
          inputBody: [],
          inputParams: []
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  }
};

// packages/client/src/api/offers.ts
import "zod";

// packages/client/src/inputs/offers.ts
import { z as z12 } from "zod";
var listOffersSchema = z12.object({
  requestId: z12.string().uuid(),
  page: z12.coerce.number().int().gt(0).optional().default(1),
  pageSize: z12.coerce.number().int().min(1).max(1e3).optional().default(50),
  sortBy: z12.enum(["createdAt", "updatedAt"]).optional().default("createdAt"),
  sortOrder: z12.enum(["asc", "desc"]).optional().default("desc"),
  status: z12.enum(["PENDING", "ACCEPTED", "IN_PROGRESS", "CANCELED", "NOT_SELECTED"]).optional()
});
var createOfferSchema = z12.object({
  notes: z12.string().optional(),
  priceItems: z12.array(
    z12.object({
      serviceName: z12.string(),
      serviceType: z12.string(),
      count: z12.number().int().gt(0),
      unitPrice: z12.number().gt(0)
    })
  ),
  requestId: z12.string().uuid()
});
var listMyOffersSchema = z12.object({
  page: z12.coerce.number().int().gt(0).optional().default(1),
  pageSize: z12.coerce.number().int().min(1).max(1e3).optional().default(50),
  sortBy: z12.enum(["createdAt", "updatedAt"]).optional().default("createdAt"),
  sortOrder: z12.enum(["asc", "desc"]).optional().default("desc"),
  status: z12.enum(["PENDING", "ACCEPTED", "IN_PROGRESS", "CANCELED", "NOT_SELECTED"]).optional()
});
var acceptOfferSchema = z12.object({
  requiredDocuments: z12.array(z12.string()).optional().default([]),
  id: z12.string().uuid()
});
var getOfferPdfSchema = z12.object({ id: z12.string().uuid() });
var getOfferDocumentsSchema = z12.object({ id: z12.string().uuid() });
var uploadOfferDocumentSchema = z12.object({
  fileId: z12.string().uuid().optional(),
  requirementId: z12.string().uuid(),
  id: z12.string().uuid()
});
var deleteOfferDocumentSchema = z12.object({
  id: z12.string().uuid(),
  docId: z12.string().uuid()
});
var uploadOfferTTSchema = z12.object({
  fileId: z12.string().uuid().optional(),
  id: z12.string().uuid()
});
var deleteOfferTTSchema = z12.object({ id: z12.string().uuid() });
var uploadVendorTTSchema = z12.object({
  fileId: z12.string().uuid().optional(),
  id: z12.string().uuid()
});
var deleteVendorTTSchema = z12.object({ id: z12.string().uuid() });
var approveVendorTTSchema = z12.object({ id: z12.string().uuid() });
var rejectVendorTTSchema = z12.object({
  reason: z12.string().optional(),
  id: z12.string().uuid()
});
var getVendorBankCertificateSchema = z12.object({
  id: z12.string().uuid()
});
var getOfferContractStatusSchema = z12.object({ id: z12.string().uuid() });

// packages/client/src/api/offers.ts
var offers_default = {
  "GET /offers": {
    schema: listOffersSchema,
    output: [
      Ok,
      BadRequest,
      Unauthorized,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "GET /offers",
        empty(input, {
          inputHeaders: [],
          inputQuery: [
            "requestId",
            "page",
            "pageSize",
            "sortBy",
            "sortOrder",
            "status"
          ],
          inputBody: [],
          inputParams: []
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "POST /offers": {
    schema: createOfferSchema,
    output: [
      Created,
      BadRequest,
      Unauthorized,
      Conflict,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "POST /offers",
        json(input, {
          inputHeaders: [],
          inputQuery: [],
          inputBody: ["notes", "priceItems", "requestId"],
          inputParams: []
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "GET /me/offers": {
    schema: listMyOffersSchema,
    output: [
      Ok,
      BadRequest,
      Unauthorized,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "GET /me/offers",
        empty(input, {
          inputHeaders: [],
          inputQuery: ["page", "pageSize", "sortBy", "sortOrder", "status"],
          inputBody: [],
          inputParams: []
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "POST /offers/{id}/accept": {
    schema: acceptOfferSchema,
    output: [
      Ok,
      BadRequest,
      Unauthorized,
      Conflict,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "POST /offers/{id}/accept",
        json(input, {
          inputHeaders: [],
          inputQuery: [],
          inputBody: ["requiredDocuments"],
          inputParams: ["id"]
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "GET /offers/{id}/pdf": {
    schema: getOfferPdfSchema,
    output: [
      Ok,
      BadRequest,
      Unauthorized,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "GET /offers/{id}/pdf",
        empty(input, {
          inputHeaders: [],
          inputQuery: [],
          inputBody: [],
          inputParams: ["id"]
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "GET /offers/{id}/documents": {
    schema: getOfferDocumentsSchema,
    output: [
      Ok,
      BadRequest,
      Unauthorized,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "GET /offers/{id}/documents",
        empty(input, {
          inputHeaders: [],
          inputQuery: [],
          inputBody: [],
          inputParams: ["id"]
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "POST /offers/{id}/documents": {
    schema: uploadOfferDocumentSchema,
    output: [
      Created,
      BadRequest,
      Unauthorized,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "POST /offers/{id}/documents",
        json(input, {
          inputHeaders: [],
          inputQuery: [],
          inputBody: ["fileId", "requirementId"],
          inputParams: ["id"]
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "DELETE /offers/{id}/documents/{docId}": {
    schema: deleteOfferDocumentSchema,
    output: [
      Ok,
      BadRequest,
      Unauthorized,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "DELETE /offers/{id}/documents/{docId}",
        empty(input, {
          inputHeaders: [],
          inputQuery: [],
          inputBody: [],
          inputParams: ["id", "docId"]
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "POST /offers/{id}/tt": {
    schema: uploadOfferTTSchema,
    output: [
      Ok,
      BadRequest,
      Unauthorized,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "POST /offers/{id}/tt",
        json(input, {
          inputHeaders: [],
          inputQuery: [],
          inputBody: ["fileId"],
          inputParams: ["id"]
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "DELETE /offers/{id}/tt": {
    schema: deleteOfferTTSchema,
    output: [
      Ok,
      BadRequest,
      Unauthorized,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "DELETE /offers/{id}/tt",
        empty(input, {
          inputHeaders: [],
          inputQuery: [],
          inputBody: [],
          inputParams: ["id"]
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "POST /offers/{id}/vendor-tt": {
    schema: uploadVendorTTSchema,
    output: [
      Ok,
      BadRequest,
      Unauthorized,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "POST /offers/{id}/vendor-tt",
        json(input, {
          inputHeaders: [],
          inputQuery: [],
          inputBody: ["fileId"],
          inputParams: ["id"]
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "DELETE /offers/{id}/vendor-tt": {
    schema: deleteVendorTTSchema,
    output: [
      Ok,
      BadRequest,
      Unauthorized,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "DELETE /offers/{id}/vendor-tt",
        empty(input, {
          inputHeaders: [],
          inputQuery: [],
          inputBody: [],
          inputParams: ["id"]
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "POST /offers/{id}/vendor-tt/approve": {
    schema: approveVendorTTSchema,
    output: [
      Ok,
      BadRequest,
      Unauthorized,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "POST /offers/{id}/vendor-tt/approve",
        empty(input, {
          inputHeaders: [],
          inputQuery: [],
          inputBody: [],
          inputParams: ["id"]
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "POST /offers/{id}/vendor-tt/reject": {
    schema: rejectVendorTTSchema,
    output: [
      Ok,
      BadRequest,
      Unauthorized,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "POST /offers/{id}/vendor-tt/reject",
        json(input, {
          inputHeaders: [],
          inputQuery: [],
          inputBody: ["reason"],
          inputParams: ["id"]
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "GET /offers/{id}/vendor-bank-certificate": {
    schema: getVendorBankCertificateSchema,
    output: [
      Ok,
      BadRequest,
      Unauthorized,
      NotFound,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "GET /offers/{id}/vendor-bank-certificate",
        empty(input, {
          inputHeaders: [],
          inputQuery: [],
          inputBody: [],
          inputParams: ["id"]
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "GET /offers/{id}/contract/status": {
    schema: getOfferContractStatusSchema,
    output: [
      Ok,
      BadRequest,
      Unauthorized,
      Forbidden,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "GET /offers/{id}/contract/status",
        empty(input, {
          inputHeaders: [],
          inputQuery: [],
          inputBody: [],
          inputParams: ["id"]
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  }
};

// packages/client/src/api/requests.ts
import "zod";

// packages/client/src/inputs/requests.ts
import { z as z14 } from "zod";
var listAdminRequestsSchema = z14.object({
  page: z14.coerce.number().int().gt(0).optional().default(1),
  pageSize: z14.coerce.number().int().min(1).max(1e3).optional().default(50),
  sortBy: z14.enum(["createdAt", "updatedAt", "projectName", "proposalDeadline"]).optional().default("createdAt"),
  sortOrder: z14.enum(["asc", "desc"]).optional().default("desc"),
  query: z14.union([z14.string(), z14.string(), z14.string()]).optional()
});
var listRequestsSchema = z14.object({
  page: z14.coerce.number().int().gt(0).optional().default(1),
  pageSize: z14.coerce.number().int().min(1).max(1e3).optional().default(50),
  sortBy: z14.enum(["createdAt", "updatedAt"]).optional().default("createdAt"),
  sortOrder: z14.enum(["asc", "desc"]).optional().default("desc"),
  status: z14.enum(["PENDING", "ACCEPTED", "IN_PROGRESS", "CANCELED", "NOT_SELECTED"]).optional()
});
var createRequestSchema = z14.object({
  ambulance4x4WithDriver: z14.object({
    amount: z14.number().int().min(0),
    equipment: z14.array(
      z14.object({ name: z14.string(), quantity: z14.number().int().min(1) })
    ).optional().default([])
  }).optional(),
  ambulance4x4WithoutDriver: z14.object({
    amount: z14.number().int().min(0),
    equipment: z14.array(
      z14.object({ name: z14.string(), quantity: z14.number().int().min(1) })
    ).optional().default([])
  }).optional(),
  ambulanceServiceWithDriver: z14.array(
    z14.object({
      type1Amount: z14.number().int().min(0),
      type2Amount: z14.number().int().min(0),
      type3Amount: z14.number().int().min(0),
      type1Equipment: z14.array(
        z14.object({ name: z14.string(), quantity: z14.number().int().min(1) })
      ).optional().default([]),
      type2Equipment: z14.array(
        z14.object({ name: z14.string(), quantity: z14.number().int().min(1) })
      ).optional().default([]),
      type3Equipment: z14.array(
        z14.object({ name: z14.string(), quantity: z14.number().int().min(1) })
      ).optional().default([])
    })
  ).optional(),
  ambulanceServiceWithoutDriver: z14.array(
    z14.object({
      type1Amount: z14.number().int().min(0),
      type2Amount: z14.number().int().min(0),
      type3Amount: z14.number().int().min(0),
      type1Equipment: z14.array(
        z14.object({ name: z14.string(), quantity: z14.number().int().min(1) })
      ).optional().default([]),
      type2Equipment: z14.array(
        z14.object({ name: z14.string(), quantity: z14.number().int().min(1) })
      ).optional().default([]),
      type3Equipment: z14.array(
        z14.object({ name: z14.string(), quantity: z14.number().int().min(1) })
      ).optional().default([])
    })
  ).optional(),
  bankGuaranteePercent: z14.number().min(0).max(20).optional(),
  dates: z14.object({ from: z14.string().datetime(), to: z14.string().datetime() }),
  doctorServices: z14.array(
    z14.object({ specialization: z14.string(), amount: z14.number().int().min(1) })
  ).optional(),
  draftId: z14.string().uuid().optional(),
  hours: z14.object({ from: z14.string(), to: z14.string() }),
  housingTransportProvided: z14.boolean(),
  isolatedWorkArea: z14.boolean(),
  kpis: z14.array(z14.string()).optional().default([]),
  location: z14.object({ name: z14.string(), latitude: z14.number(), longitude: z14.number() }).optional(),
  mealsProvided: z14.boolean(),
  medicalEquipment: z14.array(z14.object({ name: z14.string(), quantity: z14.number().int().min(1) })).optional().default([]),
  mobileClinicServices: z14.array(
    z14.object({
      amount: z14.number().int().min(1),
      type: z14.enum(["BASIC", "ADVANCED"]),
      equipment: z14.array(
        z14.object({ name: z14.string(), quantity: z14.number().int().min(1) })
      ).optional().default([])
    })
  ).optional(),
  notes: z14.string().optional(),
  nurseServices: z14.array(
    z14.object({
      amount: z14.number().int().min(1),
      type: z14.enum(["SPECIALIST", "TECHNICIAN"])
    })
  ).optional(),
  paramedicServices: z14.array(
    z14.object({
      amount: z14.number().int().min(1),
      type: z14.enum(["SPECIALIST", "TECHNICIAN"])
    })
  ).optional(),
  paymentSchedule: z14.union([
    z14.object({
      paymentType: z14.string().default("MONTHLY"),
      milestones: z14.array(
        z14.object({
          percentage: z14.number().min(0).max(100).refine(
            (val) => Number.isInteger(val / 5),
            "Must be a multiple of 5"
          )
        })
      )
    }),
    z14.object({
      paymentType: z14.string().default("DELIVERABLES"),
      milestones: z14.array(
        z14.object({
          name: z14.string(),
          percentage: z14.number().min(0).max(100).refine(
            (val) => Number.isInteger(val / 5),
            "Must be a multiple of 5"
          )
        })
      )
    })
  ]),
  projectName: z14.string(),
  projectType: z14.enum(["GOVERNMENT", "PRIVATE"]),
  proposalDeadline: z14.string().datetime(),
  requiredDocuments: z14.array(z14.string()).optional().default([])
});
var listMyRequestsSchema = z14.object({
  page: z14.coerce.number().int().gt(0).optional().default(1),
  pageSize: z14.coerce.number().int().min(1).max(1e3).optional().default(50),
  sortBy: z14.enum(["createdAt", "updatedAt"]).optional().default("createdAt"),
  sortOrder: z14.enum(["asc", "desc"]).optional().default("desc"),
  status: z14.enum(["PENDING", "ACCEPTED", "IN_PROGRESS", "CANCELED", "NOT_SELECTED"]).optional(),
  active: z14.union([z14.string().optional(), z14.boolean().optional()]).optional()
});
var listVendorRequestsSchema = z14.object({
  page: z14.coerce.number().int().gt(0).optional().default(1),
  pageSize: z14.coerce.number().int().min(1).max(1e3).optional().default(50),
  sortBy: z14.enum(["createdAt", "updatedAt"]).optional().default("createdAt"),
  sortOrder: z14.enum(["asc", "desc"]).optional().default("desc")
});
var getRequestSchema = z14.object({ id: z14.string().uuid() });
var listMyRequestDraftsSchema = z14.object({}).catchall(z14.unknown());
var createMyRequestDraftSchema = z14.object({
  data: z14.object({}).catchall(z14.unknown()),
  id: z14.string().uuid().optional()
});
var getMyRequestDraftSchema = z14.object({ id: z14.string().uuid() });
var updateMyRequestDraftSchema = z14.object({
  data: z14.object({}).catchall(z14.unknown()).optional(),
  id: z14.string().uuid()
});
var deleteMyRequestDraftSchema = z14.object({ id: z14.string().uuid() });

// packages/client/src/api/requests.ts
var requests_default = {
  "GET /admin/requests": {
    schema: listAdminRequestsSchema,
    output: [
      Ok,
      BadRequest,
      Unauthorized,
      Forbidden,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "GET /admin/requests",
        empty(input, {
          inputHeaders: [],
          inputQuery: ["page", "pageSize", "sortBy", "sortOrder", "query"],
          inputBody: [],
          inputParams: []
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "GET /requests": {
    schema: listRequestsSchema,
    output: [
      Ok,
      BadRequest,
      Unauthorized,
      Forbidden,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "GET /requests",
        empty(input, {
          inputHeaders: [],
          inputQuery: ["page", "pageSize", "sortBy", "sortOrder", "status"],
          inputBody: [],
          inputParams: []
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "POST /requests": {
    schema: createRequestSchema,
    output: [
      Created,
      BadRequest,
      Unauthorized,
      Forbidden,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "POST /requests",
        json(input, {
          inputHeaders: [],
          inputQuery: [],
          inputBody: [
            "ambulance4x4WithDriver",
            "ambulance4x4WithoutDriver",
            "ambulanceServiceWithDriver",
            "ambulanceServiceWithoutDriver",
            "bankGuaranteePercent",
            "dates",
            "doctorServices",
            "draftId",
            "hours",
            "housingTransportProvided",
            "isolatedWorkArea",
            "kpis",
            "location",
            "mealsProvided",
            "medicalEquipment",
            "mobileClinicServices",
            "notes",
            "nurseServices",
            "paramedicServices",
            "paymentSchedule",
            "projectName",
            "projectType",
            "proposalDeadline",
            "requiredDocuments"
          ],
          inputParams: []
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "GET /me/requests": {
    schema: listMyRequestsSchema,
    output: [
      Ok,
      BadRequest,
      Unauthorized,
      Forbidden,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "GET /me/requests",
        empty(input, {
          inputHeaders: [],
          inputQuery: [
            "page",
            "pageSize",
            "sortBy",
            "sortOrder",
            "status",
            "active"
          ],
          inputBody: [],
          inputParams: []
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "GET /vendor/requests": {
    schema: listVendorRequestsSchema,
    output: [
      Ok,
      BadRequest,
      Unauthorized,
      Forbidden,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "GET /vendor/requests",
        empty(input, {
          inputHeaders: [],
          inputQuery: ["page", "pageSize", "sortBy", "sortOrder"],
          inputBody: [],
          inputParams: []
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "GET /requests/{id}": {
    schema: getRequestSchema,
    output: [
      Ok,
      BadRequest,
      Unauthorized,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "GET /requests/{id}",
        empty(input, {
          inputHeaders: [],
          inputQuery: [],
          inputBody: [],
          inputParams: ["id"]
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "GET /me/request-drafts": {
    schema: listMyRequestDraftsSchema,
    output: [
      Ok,
      BadRequest,
      Unauthorized,
      Forbidden,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "GET /me/request-drafts",
        empty(input, {
          inputHeaders: [],
          inputQuery: [],
          inputBody: [],
          inputParams: []
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "POST /me/request-drafts": {
    schema: createMyRequestDraftSchema,
    output: [
      Ok,
      BadRequest,
      Unauthorized,
      Forbidden,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "POST /me/request-drafts",
        json(input, {
          inputHeaders: [],
          inputQuery: [],
          inputBody: ["data", "id"],
          inputParams: []
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "GET /me/request-drafts/{id}": {
    schema: getMyRequestDraftSchema,
    output: [
      Ok,
      BadRequest,
      Unauthorized,
      Forbidden,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "GET /me/request-drafts/{id}",
        empty(input, {
          inputHeaders: [],
          inputQuery: [],
          inputBody: [],
          inputParams: ["id"]
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "PATCH /me/request-drafts/{id}": {
    schema: updateMyRequestDraftSchema,
    output: [
      Ok,
      BadRequest,
      Unauthorized,
      Forbidden,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "PATCH /me/request-drafts/{id}",
        json(input, {
          inputHeaders: [],
          inputQuery: [],
          inputBody: ["data"],
          inputParams: ["id"]
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "DELETE /me/request-drafts/{id}": {
    schema: deleteMyRequestDraftSchema,
    output: [
      Ok,
      BadRequest,
      Unauthorized,
      Forbidden,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "DELETE /me/request-drafts/{id}",
        empty(input, {
          inputHeaders: [],
          inputQuery: [],
          inputBody: [],
          inputParams: ["id"]
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  }
};

// packages/client/src/api/settings.ts
import "zod";

// packages/client/src/inputs/settings.ts
import { z as z16 } from "zod";
var listSettingsSchema = z16.object({
  key: z16.string().optional(),
  section: z16.string().optional(),
  isActive: z16.union([z16.string().optional(), z16.boolean().optional()]).optional()
});
var createSettingSchema = z16.object({
  description: z16.string().optional(),
  isActive: z16.boolean().default(true),
  key: z16.string(),
  section: z16.string().default("general"),
  value: z16.string()
});
var bulkUpsertSettingsSchema = z16.object({
  settings: z16.array(
    z16.object({
      key: z16.string(),
      value: z16.string(),
      section: z16.string().optional().default("general"),
      description: z16.string().optional(),
      isActive: z16.boolean().optional()
    })
  ).optional()
});
var updateSettingSchema = z16.object({
  description: z16.string().optional(),
  isActive: z16.boolean().optional(),
  value: z16.string().optional(),
  section: z16.string(),
  key: z16.string()
});
var deleteSettingSchema = z16.object({
  section: z16.string(),
  key: z16.string()
});

// packages/client/src/api/settings.ts
var settings_default = {
  "GET /settings": {
    schema: listSettingsSchema,
    output: [
      Ok,
      BadRequest,
      Unauthorized,
      Forbidden,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "GET /settings",
        empty(input, {
          inputHeaders: [],
          inputQuery: ["key", "section", "isActive"],
          inputBody: [],
          inputParams: []
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "POST /settings": {
    schema: createSettingSchema,
    output: [
      Created,
      BadRequest,
      Unauthorized,
      Forbidden,
      Conflict,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "POST /settings",
        json(input, {
          inputHeaders: [],
          inputQuery: [],
          inputBody: ["description", "isActive", "key", "section", "value"],
          inputParams: []
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "PATCH /settings/bulk": {
    schema: bulkUpsertSettingsSchema,
    output: [
      Ok,
      BadRequest,
      Unauthorized,
      Forbidden,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "PATCH /settings/bulk",
        json(input, {
          inputHeaders: [],
          inputQuery: [],
          inputBody: ["settings"],
          inputParams: []
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "PATCH /settings/{section}/{key}": {
    schema: updateSettingSchema,
    output: [
      Ok,
      BadRequest,
      Unauthorized,
      Forbidden,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "PATCH /settings/{section}/{key}",
        json(input, {
          inputHeaders: [],
          inputQuery: [],
          inputBody: ["description", "isActive", "value"],
          inputParams: ["section", "key"]
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "DELETE /settings/{section}/{key}": {
    schema: deleteSettingSchema,
    output: [
      Ok,
      BadRequest,
      Unauthorized,
      Forbidden,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "DELETE /settings/{section}/{key}",
        empty(input, {
          inputHeaders: [],
          inputQuery: [],
          inputBody: [],
          inputParams: ["section", "key"]
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  }
};

// packages/client/src/api/vendors.ts
import "zod";

// packages/client/src/inputs/vendors.ts
import { z as z18 } from "zod";
var listVendorsSchema = z18.object({
  page: z18.coerce.number().int().gt(0).optional().default(1),
  pageSize: z18.coerce.number().int().min(1).max(1e3).optional().default(50),
  query: z18.union([z18.string(), z18.string(), z18.string()]).optional(),
  sortBy: z18.enum(["name", "email", "createdAt", "approved"]).optional().default("createdAt"),
  sortOrder: z18.enum(["asc", "desc"]).optional().default("desc")
});
var getCurrentVendorSchema = z18.object({}).catchall(z18.unknown());
var updateVendorSchema = z18.object({
  bankCertificate: z18.string().optional(),
  commercialRegistrationFile: z18.string().optional(),
  companyLogo: z18.string().optional(),
  firstName: z18.string().optional(),
  jobTitle: z18.string().optional(),
  lastName: z18.string().optional(),
  name: z18.string().optional(),
  nationalAddressCertificate: z18.string().optional(),
  phoneNumber: z18.string().optional(),
  redCrescentComplianceCertificate: z18.string().optional(),
  signatoryEmail: z18.string().email().optional(),
  signatoryFirstName: z18.string().optional(),
  signatoryJobTitle: z18.string().optional(),
  signatoryLastName: z18.string().optional(),
  taxCertificate: z18.string().optional(),
  zakaCertificate: z18.string().optional()
});
var acceptVendorTermsSchema = z18.object({}).catchall(z18.unknown());
var vendorsGetProfileSchema = z18.object({}).catchall(z18.unknown());
var getVendorDocumentsSchema = z18.object({ id: z18.string() });
var createVendorSchema = z18.object({
  email: z18.string().email(),
  id: z18.string(),
  name: z18.string(),
  termsAccepted: z18.boolean().default(true)
});
var updateVendorByIdSchema = z18.object({
  address: z18.union([z18.string(), z18.string().default("")]).optional(),
  bankAccountName: z18.union([z18.string(), z18.string().default("")]).optional(),
  bankName: z18.union([z18.string(), z18.string().default("")]).optional(),
  commercialRegistrationNumber: z18.union([z18.string(), z18.string().default("")]).optional(),
  email: z18.string().email().optional(),
  iban: z18.union([z18.string(), z18.string().default("")]).optional(),
  name: z18.string().optional(),
  id: z18.string()
});
var deleteVendorSchema = z18.object({ id: z18.string() });
var approveVendorSchema = z18.object({ id: z18.string() });
var rejectVendorSchema = z18.object({
  reason: z18.string().optional(),
  id: z18.string()
});
var vendorsGetVendorDocumentsSchema = z18.object({}).catchall(z18.unknown());
var createVendorRequiredDocumentSchema = z18.object({
  fileId: z18.string().optional(),
  name: z18.string(),
  reason: z18.string(),
  id: z18.string()
});
var resolveVendorRequiredDocumentSchema = z18.object({
  id: z18.string(),
  fileId: z18.string()
});

// packages/client/src/api/vendors.ts
var vendors_default = {
  "GET /vendors": {
    schema: listVendorsSchema,
    output: [
      Ok,
      BadRequest,
      Unauthorized,
      Forbidden,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "GET /vendors",
        empty(input, {
          inputHeaders: [],
          inputQuery: ["page", "pageSize", "query", "sortBy", "sortOrder"],
          inputBody: [],
          inputParams: []
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "GET /vendor/me": {
    schema: getCurrentVendorSchema,
    output: [
      Ok,
      BadRequest,
      Unauthorized,
      Forbidden,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "GET /vendor/me",
        empty(input, {
          inputHeaders: [],
          inputQuery: [],
          inputBody: [],
          inputParams: []
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "PATCH /vendor/me": {
    schema: updateVendorSchema,
    output: [
      Ok,
      BadRequest,
      Unauthorized,
      Forbidden,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "PATCH /vendor/me",
        json(input, {
          inputHeaders: [],
          inputQuery: [],
          inputBody: [
            "bankCertificate",
            "commercialRegistrationFile",
            "companyLogo",
            "firstName",
            "jobTitle",
            "lastName",
            "name",
            "nationalAddressCertificate",
            "phoneNumber",
            "redCrescentComplianceCertificate",
            "signatoryEmail",
            "signatoryFirstName",
            "signatoryJobTitle",
            "signatoryLastName",
            "taxCertificate",
            "zakaCertificate"
          ],
          inputParams: []
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "POST /vendor/me/accept-terms": {
    schema: acceptVendorTermsSchema,
    output: [
      Ok,
      BadRequest,
      Unauthorized,
      Forbidden,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "POST /vendor/me/accept-terms",
        empty(input, {
          inputHeaders: [],
          inputQuery: [],
          inputBody: [],
          inputParams: []
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "GET /vendor/me/profile": {
    schema: vendorsGetProfileSchema,
    output: [
      Ok,
      BadRequest,
      Unauthorized,
      Forbidden,
      NotFound,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "GET /vendor/me/profile",
        empty(input, {
          inputHeaders: [],
          inputQuery: [],
          inputBody: [],
          inputParams: []
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "GET /vendor/{id}/documents": {
    schema: getVendorDocumentsSchema,
    output: [
      Ok,
      BadRequest,
      Unauthorized,
      Forbidden,
      NotFound,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "GET /vendor/{id}/documents",
        empty(input, {
          inputHeaders: [],
          inputQuery: [],
          inputBody: [],
          inputParams: ["id"]
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "POST /vendor": {
    schema: createVendorSchema,
    output: [
      Ok,
      BadRequest,
      Unauthorized,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "POST /vendor",
        json(input, {
          inputHeaders: [],
          inputQuery: [],
          inputBody: ["email", "id", "name", "termsAccepted"],
          inputParams: []
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "PATCH /vendor/{id}": {
    schema: updateVendorByIdSchema,
    output: [
      Ok,
      BadRequest,
      Unauthorized,
      Forbidden,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "PATCH /vendor/{id}",
        json(input, {
          inputHeaders: [],
          inputQuery: [],
          inputBody: [
            "address",
            "bankAccountName",
            "bankName",
            "commercialRegistrationNumber",
            "email",
            "iban",
            "name"
          ],
          inputParams: ["id"]
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "DELETE /vendor/{id}": {
    schema: deleteVendorSchema,
    output: [
      Ok,
      BadRequest,
      Unauthorized,
      Forbidden,
      Conflict,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "DELETE /vendor/{id}",
        empty(input, {
          inputHeaders: [],
          inputQuery: [],
          inputBody: [],
          inputParams: ["id"]
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "PATCH /vendor/{id}/approve": {
    schema: approveVendorSchema,
    output: [
      Ok,
      BadRequest,
      Unauthorized,
      Forbidden,
      Conflict,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "PATCH /vendor/{id}/approve",
        empty(input, {
          inputHeaders: [],
          inputQuery: [],
          inputBody: [],
          inputParams: ["id"]
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "PATCH /vendor/{id}/reject": {
    schema: rejectVendorSchema,
    output: [
      Ok,
      BadRequest,
      Unauthorized,
      Forbidden,
      Conflict,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "PATCH /vendor/{id}/reject",
        json(input, {
          inputHeaders: [],
          inputQuery: [],
          inputBody: ["reason"],
          inputParams: ["id"]
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "GET /vendor/me/documents": {
    schema: vendorsGetVendorDocumentsSchema,
    output: [
      Ok,
      BadRequest,
      Unauthorized,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "GET /vendor/me/documents",
        empty(input, {
          inputHeaders: [],
          inputQuery: [],
          inputBody: [],
          inputParams: []
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "POST /vendor/{id}/request-documents": {
    schema: createVendorRequiredDocumentSchema,
    output: [
      Ok,
      BadRequest,
      Unauthorized,
      Forbidden,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "POST /vendor/{id}/request-documents",
        json(input, {
          inputHeaders: [],
          inputQuery: [],
          inputBody: ["fileId", "name", "reason"],
          inputParams: ["id"]
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "PATCH /vendor/{id}/request-documents/{fileId}/resolve": {
    schema: resolveVendorRequiredDocumentSchema,
    output: [
      Ok,
      BadRequest,
      Unauthorized,
      Forbidden,
      NotFound,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "PATCH /vendor/{id}/request-documents/{fileId}/resolve",
        empty(input, {
          inputHeaders: [],
          inputQuery: [],
          inputBody: [],
          inputParams: ["id", "fileId"]
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  }
};

// packages/client/src/api/schemas.ts
var schemas_default = {
  ...admins_default,
  ...customers_default,
  ...vendors_default,
  ...files_default,
  ...requests_default,
  ...offers_default,
  ...notifications_default,
  ...auth_default,
  ...settings_default
};

// packages/client/src/client.ts
import z21 from "zod";

// packages/client/src/http/parser.ts
import "zod";
var ParseError = class extends Error {
  data;
  constructor(data) {
    super("Validation failed");
    this.name = "ParseError";
    this.data = data;
  }
};
function parseInput(schema, input) {
  const result = schema.safeParse(input);
  if (!result.success) {
    const error = result.error.flatten((issue) => issue);
    throw new ParseError(error);
  }
  return result.data;
}

// packages/client/src/client.ts
var optionsSchema = z21.object({
  token: z21.union([
    z21.string(),
    z21.function().returns(z21.union([z21.string(), z21.promise(z21.string())]))
  ]).optional().transform(async (token) => {
    if (!token) return void 0;
    if (typeof token === "function") {
      token = await Promise.resolve(token());
    }
    return `Bearer ${token}`;
  }).describe(
    "Bearer token for authentication. Can be a string or a function that returns a string."
  ),
  fetch: fetchType.describe(
    "Custom fetch implementation. Defaults to globalThis.fetch."
  ),
  baseUrl: z21.union([
    z21.string(),
    z21.function().returns(z21.union([z21.string(), z21.promise(z21.string())]))
  ]).transform(async (baseUrl) => {
    if (typeof baseUrl === "function") {
      return Promise.resolve(baseUrl());
    }
    return baseUrl;
  }).describe(
    "Base URL of the API server. Can be a string or a function that returns a string."
  ),
  headers: z21.record(z21.string()).optional().describe("Default headers to include in all requests."),
  skipValidation: z21.boolean().optional().describe(
    "Skip request input validation. Client options and TypeScript types still enforce correct usage."
  )
});
var ColdAmbulance = class {
  options;
  constructor(options) {
    this.options = options;
  }
  /** Sends a request and returns the unwrapped response data. Delegates to the standalone {@link request} function. */
  async request(endpoint, input, options) {
    return request(this, endpoint, input, options).then(function unwrap(it) {
      if (it instanceof APIResponse) {
        return it.data;
      }
      return it;
    });
  }
  /** Builds a ready-to-send request without sending it. Delegates to the standalone {@link prepare} function. */
  async prepare(endpoint, input, options) {
    return prepare(this, endpoint, input, options);
  }
  async defaultHeaders() {
    const options = await optionsSchema.parseAsync(this.options);
    return {
      ...{ authorization: options["token"] },
      ...options.headers
    };
  }
  async defaultInputs() {
    const options = await optionsSchema.parseAsync(this.options);
    return {};
  }
  setOptions(options) {
    this.options = {
      ...this.options,
      ...options
    };
  }
};
async function request(client, endpoint, input, requestOptions) {
  const route = schemas_default[endpoint];
  const options = await optionsSchema.parseAsync(client.options);
  const withDefaultInputs = Object.assign({}, {}, input);
  const parsedInput = options.skipValidation ? withDefaultInputs : parseInput(route.schema, withDefaultInputs);
  const result = await route.dispatch(parsedInput, {
    fetch: options.fetch,
    interceptors: [
      createHeadersInterceptor(
        { ...{ authorization: options["token"] }, ...options.headers },
        requestOptions?.headers ?? {}
      ),
      createBaseUrlInterceptor(options.baseUrl)
    ],
    signal: requestOptions?.signal
  });
  return result;
}
async function prepare(client, endpoint, input, requestOptions) {
  const route = schemas_default[endpoint];
  const options = await optionsSchema.parseAsync(client.options);
  const withDefaultInputs = Object.assign({}, {}, input);
  const parsedInput = options.skipValidation ? withDefaultInputs : parseInput(route.schema, withDefaultInputs);
  const interceptors = [
    createHeadersInterceptor(
      { ...{ authorization: options["token"] }, ...options.headers },
      requestOptions?.headers ?? {}
    ),
    createBaseUrlInterceptor(options.baseUrl)
  ];
  let config = route.toRequest(parsedInput);
  if (requestOptions?.signal) {
    config = {
      ...config,
      init: {
        ...config.init,
        signal: requestOptions.signal
      }
    };
  }
  for (const interceptor of interceptors) {
    if (interceptor.before) {
      config = await interceptor.before(config);
    }
  }
  return {
    ...config,
    parse: (response) => parse2(route.output, response, (d) => d)
  };
}
export {
  APIError,
  APIResponse,
  Accepted,
  BadGateway,
  BadRequest,
  ColdAmbulance,
  Conflict,
  Created,
  Forbidden,
  GatewayTimeout,
  Gone,
  InternalServerError,
  MethodNotAllowed,
  NoContent,
  NotAcceptable,
  NotFound,
  NotImplemented,
  Ok,
  ParseError,
  PayloadTooLarge,
  PaymentRequired,
  PreconditionFailed,
  ServiceUnavailable,
  TooManyRequests,
  Unauthorized,
  UnprocessableEntity,
  UnsupportedMediaType,
  acceptCustomerTermsSchema,
  acceptOfferSchema,
  acceptVendorTermsSchema,
  approveCustomerSchema,
  approveOfferDocumentSchema,
  approveTTReviewSchema,
  approveVendorSchema,
  approveVendorTTSchema,
  bulkUpsertSettingsSchema,
  changeOwnPasswordSchema,
  createAdminSchema,
  createCustomerRequiredDocumentSchema,
  createCustomerSchema,
  createMyRequestDraftSchema,
  createOfferSchema,
  createRequestSchema,
  createSettingSchema,
  createVendorRequiredDocumentSchema,
  createVendorSchema,
  deleteAdminSchema,
  deleteCustomerSchema,
  deleteMyRequestDraftSchema,
  deleteOfferDocumentSchema,
  deleteOfferTTSchema,
  deleteSettingSchema,
  deleteVendorSchema,
  deleteVendorTTSchema,
  firebaseAuthActionSchema,
  forgotPasswordSchema,
  getCurrentCustomerSchema,
  getCurrentUserSchema,
  getCurrentVendorSchema,
  getCustomerDocumentsByIdSchema,
  getCustomerDocumentsSchema,
  getDocumentReviewSchema,
  getFileAccessSchema,
  getFileByIdSchema,
  getFilesSchema,
  getMyRequestDraftSchema,
  getOfferContractStatusSchema,
  getOfferDocumentsSchema,
  getOfferPdfSchema,
  getOfferTimelineSchema,
  getProfileSchema,
  getRequestSchema,
  getSignedDocumentLinksSchema,
  getSignedUrlSchema,
  getVendorBankCertificateSchema,
  getVendorDocumentsSchema,
  listAdminRequestsSchema,
  listAdminsSchema,
  listCustomersSchema,
  listDocumentReviewsSchema,
  listMyOffersSchema,
  listMyRequestDraftsSchema,
  listMyRequestsSchema,
  listNotificationsSchema,
  listOffersSchema,
  listRequestsSchema,
  listSettingsSchema,
  listTTReviewsSchema,
  listVendorRequestsSchema,
  listVendorsSchema,
  markAllNotificationsAsReadSchema,
  markFileAsUsedSchema,
  markNotificationAsReadSchema,
  parseInput,
  passwordChangedSchema,
  prepare,
  rejectCustomerSchema,
  rejectOfferDocumentSchema,
  rejectTTReviewSchema,
  rejectVendorSchema,
  rejectVendorTTSchema,
  request,
  resolveCustomerRequiredDocumentSchema,
  resolveVendorRequiredDocumentSchema,
  updateAdminSchema,
  updateCurrentAdminSchema,
  updateCustomerByIdSchema,
  updateCustomerSchema,
  updateMyRequestDraftSchema,
  updateSettingSchema,
  updateVendorByIdSchema,
  updateVendorSchema,
  uploadOfferDocumentSchema,
  uploadOfferTTSchema,
  uploadVendorTTSchema,
  vendorsGetProfileSchema,
  vendorsGetVendorDocumentsSchema
};
//# sourceMappingURL=index.js.map
